import {useCallback, useEffect, useRef} from 'react';

/** Android waits this long after the last change before saving (AutosaveController). */
export const AUTOSAVE_DELAY_MS = 600;

interface Lane {
  timer?: number;
  running?: Promise<void>;
  /** A change arrived while the lane was saving: save once more when it finishes. */
  again: boolean;
  persist: () => Promise<void>;
}

/**
 * Android's AutosaveController, one lane per key: a change schedules the
 * lane's save after a quiet moment; changes coalesce, and a lane never runs
 * two saves at once (a change made while one is in flight saves again after
 * it, with what is current then). `persist` reads the latest state itself.
 */
export function useAutosaveLanes(onError: (message: string) => void) {
  const lanes = useRef(new Map<string, Lane>());
  const errorRef = useRef(onError);
  errorRef.current = onError;

  /**
   * Resolves once the lane is idle: after the save in flight AND the
   * follow-up it starts for a change that arrived behind it (and so on).
   * Awaiting only the in-flight save let a flush resolve while the save
   * with the current state was still on its way.
   */
  const settled = (lane: Lane): Promise<void> =>
    // `finally` has cleared or replaced `lane.running` by the time this `.then` runs.
    lane.running ? lane.running.then(() => settled(lane)) : Promise.resolve();

  const run = useCallback((key: string): Promise<void> => {
    const lane = lanes.current.get(key);
    if (!lane) return Promise.resolve();
    if (lane.running) {
      lane.again = true;
      return settled(lane);
    }
    lane.again = false;
    lane.running = lane.persist()
      .catch(e => errorRef.current(e instanceof Error ? e.message : String(e)))
      .finally(() => {
        lane.running = undefined;
        if (lane.again) void run(key);
      });
    return settled(lane);
  }, []);

  /** Saves `key` after the quiet moment, with `persist` (the latest one given wins). */
  const schedule = useCallback((key: string, persist: () => Promise<void>) => {
    const lane = lanes.current.get(key) ?? {again: false, persist};
    lane.persist = persist;
    lanes.current.set(key, lane);
    window.clearTimeout(lane.timer);
    lane.timer = window.setTimeout(() => {
      lane.timer = undefined;
      void run(key);
    }, AUTOSAVE_DELAY_MS);
  }, [run]);

  /** Drops a waiting save (the thing it would write was deleted). An in-flight one finishes. */
  const cancel = useCallback((key: string) => {
    const lane = lanes.current.get(key);
    if (!lane) return;
    window.clearTimeout(lane.timer);
    lane.timer = undefined;
    lane.again = false;
    lanes.current.delete(key);
  }, []);

  /** Sends every waiting change now and waits for every save (before a status change, or leaving). */
  const flush = useCallback(async () => {
    const pending: Promise<void>[] = [];
    lanes.current.forEach((lane, key) => {
      if (lane.timer !== undefined) {
        window.clearTimeout(lane.timer);
        lane.timer = undefined;
        pending.push(run(key));
      } else if (lane.running) {
        pending.push(settled(lane));
      }
    });
    await Promise.all(pending);
  }, [run]);

  // Leaving the page sends what is still waiting.
  useEffect(() => () => { void flush(); }, [flush]);

  return {schedule, cancel, flush};
}
